import { Controller } from 'stimulus'
import Sortable from 'sortablejs'

function renderSurveyStarPreview(select) {
  if (!select) return

  const scale = Math.min(10, Math.max(2, parseInt(select.value, 10) || 5))
  const container = select.closest('.survey-creator__rating-scale')
  const panel = select.closest('[data-target~="survey-creator.questionPanel"]') || select.closest('.survey-creator__question-card')
  const preview =
    (container && container.querySelector('.survey-creator__star-preview')) ||
    (panel && panel.querySelector('.survey-creator__star-preview'))
  if (!preview) return

  let html = ''
  for (let i = 0; i < scale; i++) {
    html += '<i class="fa-regular fa-star" aria-hidden="true"></i>'
  }
  preview.innerHTML = html
}

export default class extends Controller {
  static targets = [
    'questionList',
    'questionPanel',
    'questionTemplate',
    'answerTemplate',
    'positionField',
    'destroyField',
    'questionText',
    'questionBadge',
    'answers',
    'errorSummary',
    'dirtyLabel',
    'statusField',
    'coverInput',
    'questionCountMeta',
    'typeMenu'
  ]

  connect() {
    this.dirty = false
    this.allowNavigation = false
    this.boundBeforeUnload = this.beforeUnload.bind(this)
    this.boundCloseMenus = this.closeTypeMenusOnOutsideClick.bind(this)
    this.boundRatingScaleChange = this.onRatingScaleChange.bind(this)
    window.addEventListener('beforeunload', this.boundBeforeUnload)
    document.addEventListener('click', this.boundCloseMenus)
    // Native listener: Stimulus actions + Select2/jQuery change are unreliable for selects here.
    this.element.addEventListener('change', this.boundRatingScaleChange)

    try {
      this.initSortable()
    } catch (e) {
      // Sortable is optional; keep add/remove working if it fails to init.
    }

    this.syncPositionsFromList()
    this.updateQuestionCount()
    this.initRatingScaleSelect2()
    this.syncAllStarPreviews()
    this.autoGrowAllQuestionTexts()
    this.focusFirstError()
    this.selectFirstQuestion()
  }

  autoGrowAllQuestionTexts() {
    this.questionTextTargets.forEach((field) => this.autoGrowQuestionText({ currentTarget: field }))
  }

  autoGrowQuestionText(event) {
    const field = event && (event.currentTarget || event.target)
    if (!field || field.tagName !== 'TEXTAREA') return

    field.style.height = 'auto'
    field.style.height = `${field.scrollHeight}px`
  }

  syncAllStarPreviews() {
    this.ratingScaleSelects().forEach((select) => this.renderStarPreview(select))
  }

  ratingScaleSelects() {
    return Array.from(this.element.querySelectorAll('select.survey-creator__rating-scale-select, select[name*="[rating_scale]"]'))
  }

  onRatingScaleChange(event) {
    const select = this.ratingScaleSelectFrom(event && event.target)
    if (!select) return
    this.renderStarPreview(select)
  }

  ratingScaleSelectFrom(el) {
    if (!el) return null
    if (el.tagName === 'SELECT' && (el.classList.contains('survey-creator__rating-scale-select') || (el.name && el.name.indexOf('[rating_scale]') !== -1))) {
      return el
    }
    if (el.closest) {
      return el.closest('select.survey-creator__rating-scale-select, select[name*="[rating_scale]"]')
    }
    return null
  }

  disconnect() {
    window.removeEventListener('beforeunload', this.boundBeforeUnload)
    document.removeEventListener('click', this.boundCloseMenus)
    this.element.removeEventListener('change', this.boundRatingScaleChange)
    this.destroyRatingScaleSelect2()
    if (this.sortable) this.sortable.destroy()
  }

  initSortable() {
    if (!this.hasQuestionListTarget) return

    this.sortable = Sortable.create(this.questionListTarget, {
      handle: '.grabbable',
      animation: 150,
      draggable: '.survey-creator__question-card',
      filter: '.is-destroyed',
      onMove: (evt) => !this.isDestroyed(evt.related),
      onEnd: () => {
        this.syncPositionsFromList()
        this.markDirty()
      }
    })
  }

  toggleTypeMenu(event) {
    event.preventDefault()
    event.stopPropagation()
    const menu = event.currentTarget.closest('[data-target~="survey-creator.typeMenu"]')
    if (!menu) return

    const opening = !menu.classList.contains('is-open')
    this.closeAllTypeMenus()
    if (opening) menu.classList.add('is-open')
  }

  closeAllTypeMenus() {
    this.typeMenuTargets.forEach((menu) => menu.classList.remove('is-open'))
  }

  closeTypeMenusOnOutsideClick(event) {
    if (event.target.closest('[data-target~="survey-creator.typeMenu"]')) return
    this.closeAllTypeMenus()
  }

  selectFirstQuestion() {
    const panels = this.visiblePanels()
    if (panels.length > 0) this.selectByUid(panels[0].dataset.questionUid)
  }

  focusFirstError() {
    const invalid = this.element.querySelector('.is-invalid, .invalid-feedback')
    if (!invalid) return

    const panel = invalid.closest('[data-target~="survey-creator.questionPanel"]')
    if (panel) {
      this.selectByUid(panel.dataset.questionUid)
      invalid.scrollIntoView({ block: 'center', behavior: 'smooth' })
    } else {
      invalid.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }

  selectQuestion(event) {
    const panel = event.currentTarget.closest('[data-target~="survey-creator.questionPanel"]')
    if (!panel || panel.classList.contains('is-destroyed')) return
    this.selectByUid(panel.dataset.questionUid)
  }

  selectByUid(uid) {
    if (!uid) return

    this.questionPanelTargets.forEach((panel) => {
      const active = panel.dataset.questionUid === uid && !this.isDestroyed(panel)
      panel.classList.toggle('is-active', active)
    })
  }

  addQuestion(event) {
    event.preventDefault()
    event.stopPropagation()

    const type = event.currentTarget.getAttribute('data-question-type')
    const template = this.findQuestionTemplate(type)
    if (!template || !this.hasQuestionListTarget) return

    // Nested attribute indexes must be integer-like; Rails strong params drops keys like "new-123".
    const uid = this.nextNestedIndex()
    const html = this.blueprintHtml(template).replace(/NEW_RECORD/g, uid)
    if (!html.trim()) return

    this.questionListTarget.insertAdjacentHTML('beforeend', html)
    this.closeAllTypeMenus()
    this.syncPositionsFromList()
    this.updateQuestionCount()
    this.selectByUid(uid)
    this.markDirty()

    const panel = this.element.querySelector(`[data-target~="survey-creator.questionPanel"][data-question-uid="${uid}"]`)
    if (panel) this.initRatingScaleSelect2(panel)
    const textField = panel && panel.querySelector('[data-target~="survey-creator.questionText"]')
    if (textField) {
      this.autoGrowQuestionText({ currentTarget: textField })
      textField.focus()
      panel.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }

  removeQuestion(event) {
    event.preventDefault()
    event.stopPropagation()
    const panel = event.currentTarget.closest('[data-target~="survey-creator.questionPanel"]')
    if (!panel) return

    const afterRemoval = () => {
      this.syncPositionsFromList()
      this.updateQuestionCount()
      this.markDirty()
      this.selectFirstQuestion()
    }

    // New (unsaved) questions can be removed from the DOM without a warning.
    if (!this.isPersistedRecord(panel)) {
      this.destroyRatingScaleSelect2(panel)
      panel.remove()
      afterRemoval()
      return
    }

    this.confirmDestructiveRemoval(
      'This action will remove all related information for this record. These changes will not be final until you save the form.',
      'Remove this question?',
      () => {
        const destroyField = panel.querySelector('[data-target~="survey-creator.destroyField"]')
        if (destroyField) destroyField.value = '1'
        panel.classList.add('is-destroyed')
        panel.classList.remove('is-active')
        panel.style.display = 'none'
        afterRemoval()
      }
    )
  }

  moveQuestionUp(event) {
    event.preventDefault()
    event.stopPropagation()
    const panel = event.currentTarget.closest('[data-target~="survey-creator.questionPanel"]')
    if (!panel) return
    const prev = this.adjacentVisiblePanel(panel, 'previousElementSibling')
    if (prev) {
      this.questionListTarget.insertBefore(panel, prev)
      this.syncPositionsFromList()
      this.markDirty()
    }
  }

  moveQuestionDown(event) {
    event.preventDefault()
    event.stopPropagation()
    const panel = event.currentTarget.closest('[data-target~="survey-creator.questionPanel"]')
    if (!panel) return
    const next = this.adjacentVisiblePanel(panel, 'nextElementSibling')
    if (next) {
      this.questionListTarget.insertBefore(next, panel)
      this.syncPositionsFromList()
      this.markDirty()
    }
  }

  adjacentVisiblePanel(panel, direction) {
    let sibling = panel[direction]
    while (sibling) {
      if (
        sibling.matches('[data-target~="survey-creator.questionPanel"]') &&
        !this.isDestroyed(sibling)
      ) {
        return sibling
      }
      sibling = sibling[direction]
    }
    return null
  }

  addAnswer(event) {
    event.preventDefault()
    event.stopPropagation()
    const panel = event.currentTarget.closest('[data-target~="survey-creator.questionPanel"]')
    if (!panel) return

    const type =
      event.currentTarget.getAttribute('data-question-type') || panel.getAttribute('data-question-type')
    const template = this.findAnswerTemplate(type)
    const answers = panel.querySelector('[data-target~="survey-creator.answers"]')
    if (!template || !answers) return

    const qIndex = this.questionIndexFromPanel(panel)
    if (qIndex == null) return

    // Nested attribute indexes must be integer-like; Rails strong params drops keys like "answer-123".
    const answerIndex = this.nextNestedIndex()
    const html = this.blueprintHtml(template)
      .replace(/__QINDEX__/g, qIndex)
      .replace(/ANSWER_RECORD/g, answerIndex)

    if (!html.trim()) return
    answers.insertAdjacentHTML('beforeend', html)
    this.markDirty()
  }

  removeAnswer(event) {
    event.preventDefault()
    event.stopPropagation()
    const answer = event.currentTarget.closest('[data-target~="survey-creator.answer"]')
    if (!answer) return

    // New (unsaved) answers — including those on new questions — leave the DOM entirely.
    if (!this.isPersistedRecord(answer)) {
      answer.remove()
      this.markDirty()
      return
    }

    this.confirmDestructiveRemoval(
      'This action will remove all related information for this record. These changes will not be final until you save the form.',
      'Remove this answer?',
      () => {
        const destroyField = answer.querySelector("input[name*='_destroy']")
        if (destroyField) destroyField.value = '1'
        answer.style.display = 'none'
        this.markDirty()
      }
    )
  }

  isPersistedRecord(container) {
    if (!container) return false
    // Use this record's own id field (direct child), not nested answer/question ids.
    const idField =
      container.querySelector(':scope > input[name$="[id]"]') ||
      Array.from(container.children).find(
        (el) => el.tagName === 'INPUT' && /\[id\]$/.test(el.getAttribute('name') || '')
      )
    return !!(idField && String(idField.value || '').trim())
  }

  confirmDestructiveRemoval(message, fallbackPrompt, onConfirm) {
    if (window.Swal && typeof window.Swal.fire === 'function') {
      window.Swal.fire({
        title: 'Are you sure?',
        text: message,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No'
      }).then((result) => {
        if (result.isConfirmed) onConfirm()
      })
    } else if (window.confirm(`${message}\n\n${fallbackPrompt}`)) {
      onConfirm()
    }
  }

  setStatus(event) {
    event.preventDefault()
    const status = event.currentTarget.dataset.status
    if (!this.hasStatusFieldTarget) return

    this.statusFieldTarget.value = status
    const toggle = event.currentTarget.closest('.survey-creator__status')
    if (toggle) {
      toggle.querySelectorAll('.survey-creator__status-option').forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.status === status)
      })
    }
    this.markDirty()
  }

  syncAudienceSelection(event) {
    const option = event.currentTarget.closest('.survey-creator__audience-option')
    if (!option) return

    option.parentElement.querySelectorAll('.survey-creator__audience-option').forEach((el) => {
      el.classList.remove('is-selected')
    })
    option.classList.add('is-selected')
    this.markDirty()
  }

  renderStarPreview(select) {
    renderSurveyStarPreview(select)
  }

  initRatingScaleSelect2(scope = this.element) {
    if (!window.$) return

    window.$(scope).find('select.survey-creator__rating-scale-select').each((_, select) => {
      const $select = window.$(select)
      if ($select.hasClass('select2-hidden-accessible')) return

      $select.select2({
        width: '5.5rem',
        minimumResultsForSearch: Infinity
      })
      $select.on('select2:select.select2Rating select2:clear.select2Rating select2:unselect.select2Rating', (e) => {
        e.target.dispatchEvent(new Event('change', { bubbles: true }))
      })
    })
  }

  destroyRatingScaleSelect2(scope = this.element) {
    if (!window.$) return

    window.$(scope).find('select.survey-creator__rating-scale-select').each((_, select) => {
      const $select = window.$(select)
      if (!$select.data('select2')) return

      $select.off('.select2Rating')
      $select.select2('destroy')
    })
  }

  triggerCoverUpload(event) {
    const input = this.coverFileInput()
    if (!input) return

    // Ignore the synthetic click we fire on the file input itself
    if (event && event.target === input) return

    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    input.click()
  }

  onCoverSelected(event) {
    this.markDirty()
    const input = event.currentTarget
    const file = input.files && input.files[0]
    if (!file || !file.type.startsWith('image/')) return

    const cover = input.closest('.survey-creator__cover')
    if (!cover) return

    const url = URL.createObjectURL(file)
    let image = cover.querySelector('.survey-creator__cover-image')
    if (!image) {
      cover.querySelector('.survey-creator__cover-empty')?.remove()
      image = document.createElement('img')
      image.className = 'survey-creator__cover-image'
      cover.insertBefore(image, input)

      const actions = document.createElement('div')
      actions.className = 'survey-creator__cover-actions'
      actions.innerHTML = '<button type="button" class="btn btn-sm btn-light">Change image</button>'
      cover.insertBefore(actions, input)
    }
    image.src = url
    cover.classList.add('has-image')
  }

  coverFileInput() {
    if (this.hasCoverInputTarget) return this.coverInputTarget
    return this.element.querySelector('[data-target~="survey-creator.coverInput"]')
  }

  findQuestionTemplate(type) {
    return this.findTemplateByType(this.questionTemplateTargets, type)
  }

  findAnswerTemplate(type) {
    return this.findTemplateByType(this.answerTemplateTargets, type)
  }

  findTemplateByType(targets, type) {
    return targets.find((el) => el.getAttribute('data-question-type') === type)
  }

  blueprintHtml(template) {
    if (!template) return ''
    if (template.tagName === 'TEMPLATE' && template.content) {
      const holder = document.createElement('div')
      holder.appendChild(template.content.cloneNode(true))
      return holder.innerHTML
    }
    return template.innerHTML || template.textContent || ''
  }

  syncPositionsFromList() {
    this.visiblePanels().forEach((panel, index) => {
      const positionField = panel.querySelector('[data-target~="survey-creator.positionField"]')
      if (positionField) positionField.value = index + 1

      const badge = panel.querySelector('[data-target~="survey-creator.questionBadge"]')
      if (badge) badge.textContent = `Q${index + 1}`
    })
  }

  updateQuestionCount() {
    if (!this.hasQuestionCountMetaTarget) return
    const count = this.visiblePanels().length
    this.questionCountMetaTarget.textContent = `${count} question${count === 1 ? '' : 's'}`
  }

  visiblePanels() {
    return this.questionPanelTargets.filter((panel) => !this.isDestroyed(panel) && panel.style.display !== 'none')
  }

  questionIndexFromPanel(panel) {
    const sample = panel.querySelector("input[name*='[survey_questions_attributes]']")
    if (!sample) return null
    const match = sample.name.match(/\[survey_questions_attributes\]\[([^\]]+)\]/)
    return match ? match[1] : null
  }

  nextNestedIndex() {
    return `${Date.now()}${Math.floor(Math.random() * 100000)}`
  }

  isDestroyed(panel) {
    const destroyField = panel.querySelector('[data-target~="survey-creator.destroyField"]')
    return panel.classList.contains('is-destroyed') || (destroyField && destroyField.value === '1')
  }

  markDirty(event) {
    const select = this.ratingScaleSelectFrom(event && event.target)
    if (select) this.renderStarPreview(select)

    this.dirty = true
    if (this.hasDirtyLabelTarget) this.dirtyLabelTarget.classList.remove('d-none')
  }

  allowLeave() {
    this.allowNavigation = true
    this.dirty = false
  }

  beforeUnload(event) {
    if (!this.dirty || this.allowNavigation) return
    event.preventDefault()
    event.returnValue = ''
  }
}
